import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Footer from '../home/components/Footer';

export default function VIPPage() {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentLevel] = useState('Commander');
  const [currentProgress] = useState({
    totalBets: 75000,
    gamesPlayed: 250,
    activeDays: 28
  });

  // 로그인 체크
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // 로그인하지 않은 경우 아무것도 렌더링하지 않음
  if (!isAuthenticated) {
    return null;
  }

  const vipLevels = [
    {
      name: 'Ranger',
      badge: '🌟',
      color: '#00D8FF',
      description: 'VIP Entry Stage - Natural upgrade with regular activity after registration',
      requirements: {
        totalBets: 8000,
        gamesPlayed: 80,
        activeDays: 10
      },
      benefits: [
        { icon: 'ri-percent-line', text: 'Net Loss Cashback', value: '1%' },
        { icon: 'ri-trophy-line', text: 'Tournament Access', value: 'Weekly Basic Tournament' },
        { icon: 'ri-customer-service-2-line', text: 'VIP Support', value: 'Email Support' }
      ]
    },
    {
      name: 'Commander',
      badge: '⚡',
      color: '#29A5FF',
      description: 'Real VIP Starting Point - Reward experience and withdrawal benefits stage',
      requirements: {
        totalBets: 50000,
        gamesPlayed: 200,
        activeDays: 25
      },
      benefits: [
        { icon: 'ri-percent-line', text: 'Net Loss Cashback', value: '2%' },
        { icon: 'ri-flashlight-line', text: 'Fast Withdrawal', value: 'Processed within 2-4 hours' },
        { icon: 'ri-refresh-line', text: 'Free Spins', value: '25 per week' },
        { icon: 'ri-trophy-line', text: 'Tournament Access', value: 'Premium Tournament' },
        { icon: 'ri-customer-service-2-line', text: 'Priority Support', value: '24/7 Chat Support' }
      ]
    },
    {
      name: 'Guardian',
      badge: '💎',
      color: '#4B3FAE',
      description: 'Main VIP tier with dedicated manager and priority support',
      requirements: {
        totalBets: 200000,
        gamesPlayed: 500,
        activeDays: 50
      },
      benefits: [
        { icon: 'ri-percent-line', text: 'Net Loss Cashback', value: '3%' },
        { icon: 'ri-flashlight-line', text: 'Instant Withdrawal', value: 'Processed within 1 hour' },
        { icon: 'ri-refresh-line', text: 'Free Spins', value: '75 per week' },
        { icon: 'ri-gift-line', text: 'VIP Exclusive Promotion', value: 'VIP Only Benefits' },
        { icon: 'ri-calendar-event-line', text: 'VIP Exclusive Events', value: 'Special Event Access' },
        { icon: 'ri-user-star-line', text: 'Dedicated Manager', value: '1:1 Personal Manager' }
      ]
    },
    {
      name: 'Legend',
      badge: '👑',
      color: '#EFBF65',
      description: 'Premium Customer - Event invitation and external benefits target',
      requirements: {
        totalBets: 1000000,
        gamesPlayed: 1200,
        activeDays: 80
      },
      benefits: [
        { icon: 'ri-vip-crown-line', text: 'Net Loss Cashback', value: '5%' },
        { icon: 'ri-flashlight-line', text: 'Instant Withdrawal', value: 'Immediate Processing' },
        { icon: 'ri-user-star-line', text: 'Dedicated VIP Manager', value: '24/7 1:1 Personal Support' },
        { icon: 'ri-gift-line', text: 'Birthday Premium Package', value: 'Special Gift Provided' },
        { icon: 'ri-plane-line', text: 'Offline Invitation Event', value: 'Exclusive Event Invitation' },
        { icon: 'ri-trophy-line', text: 'Legend Exclusive Tournament', value: 'Top-tier Tournament' },
        { icon: 'ri-refresh-line', text: 'Free Spins', value: '150 per week' }
      ]
    }
  ];

  const getCurrentLevelIndex = () => {
    return vipLevels.findIndex(level => level.name === currentLevel);
  };

  const getNextLevel = () => {
    const currentIndex = getCurrentLevelIndex();
    if (currentIndex < vipLevels.length - 1) {
      return vipLevels[currentIndex + 1];
    }
    return null;
  };

  const calculateProgress = (requirement: number, current: number) => {
    return Math.min((current / requirement) * 100, 100);
  };

  const nextLevel = getNextLevel();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0D0E11] via-[#111319] to-[#16181D]">
      {/* Header */}
      <header className="bg-[#1a1c24] border-b border-[#FFD349]/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo & Mobile Balance */}
            <div className="flex flex-col gap-2">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-slot-machine-line text-[#FFD349] text-2xl"></i>
                </div>
                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#FFD349] to-[#FFC107] bg-clip-text text-transparent" style={{ fontFamily: 'Orbitron, sans-serif', textShadow: '0 0 30px rgba(255, 211, 73, 0.3), 0 0 60px rgba(255, 211, 73, 0.2), 0 5px 10px rgba(0, 0, 0, 0.5), 0 10px 20px rgba(0, 0, 0, 0.3), 0 15px 30px rgba(0, 0, 0, 0.2)' }}>
                  DIGITWISE
                </h1>
              </Link>
              
              {/* Mobile Balance - Below Logo */}
              {user && (
                <div className="md:hidden flex items-center justify-center gap-1 bg-[#0D0E11] border border-[#FFD349]/20 rounded px-3 py-1.5 mx-auto w-fit">
                  <i className="ri-wallet-3-line text-[#FFD349] text-sm"></i>
                  <span className="text-white font-semibold text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    ${user.balance.toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Home
              </Link>
              <Link to="/dashboard" className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Dashboard
              </Link>
              <Link to="/game-lobby" className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Games
              </Link>
              <Link to="/wallet" className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Wallet
              </Link>
              <Link to="/vip" className="text-[#FFD349] text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                VIP
              </Link>
              <Link to="/profile" className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Profile
              </Link>
            </nav>

            {/* Desktop Balance & Logout */}
            <div className="hidden md:flex items-center gap-4">
              <div className="flex items-center gap-3 bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-2">
                <i className="ri-wallet-3-line text-[#FFD349] text-lg"></i>
                <div>
                  <p className="text-white/60 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>Balance</p>
                  <p className="text-[#FFD349] font-bold text-lg" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                    ${user?.balance.toLocaleString() || '0'}
                  </p>
                </div>
              </div>
              <button
                onClick={logout}
                className="bg-gradient-to-r from-[#FFD349] to-[#FFC107] text-black px-6 py-2 rounded-lg font-bold text-sm hover:shadow-[0px_0px_20px_rgba(255,211,73,0.6)] transition-all whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Logout
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white p-2 hover:text-[#FFD349] transition-colors cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pt-4 border-t border-[#FFD349]/20">
              <nav className="flex flex-col gap-3">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium py-2 px-4 rounded-lg hover:bg-[#0D0E11] cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-home-line mr-2"></i>
                  Home
                </Link>
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium py-2 px-4 rounded-lg hover:bg-[#0D0E11] cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-dashboard-line mr-2"></i>
                  Dashboard
                </Link>
                <Link
                  to="/wallet"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium py-2 px-4 rounded-lg hover:bg-[#0D0E11] cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-wallet-line mr-2"></i>
                  Wallet
                </Link>
                <Link
                  to="/vip"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#FFD349] text-sm font-medium py-2 px-4 rounded-lg bg-[#0D0E11] cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-vip-crown-line mr-2"></i>
                  VIP
                </Link>
                <Link
                  to="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium py-2 px-4 rounded-lg hover:bg-[#0D0E11] cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-user-line mr-2"></i>
                  Profile
                </Link>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout();
                  }}
                  className="text-white/80 hover:text-[#FFD349] transition-colors text-sm font-medium py-2 px-4 rounded-lg hover:bg-[#0D0E11] text-left cursor-pointer"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <i className="ri-logout-box-line mr-2"></i>
                  Logout
                </button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Fixed Background */}
      <div 
        className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Luxurious%20VIP%20casino%20lounge%20with%20deep%20purple%20and%20golden%20gradient%20background%20featuring%20elegant%20crown%20symbols%20and%20premium%20diamond%20particles%20with%20sophisticated%20bokeh%20effects%20and%20royal%20atmosphere%20perfect%20for%20exclusive%20VIP%20gaming%20platform%20with%20mystical%20lighting%20and%20cosmic%20stars%20in%20dark%20elegant%20color%20scheme&width=1920&height=1080&seq=vip-bg-fixed-001&orientation=landscape)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="pt-24 md:pt-32 pb-12 md:pb-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              <i className="ri-vip-crown-fill text-[#EFBF65] mr-2 md:mr-4"></i>
              VIP Club
            </h1>
            <p className="text-white/80 text-base md:text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed px-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Experience exclusive benefits, premium rewards, and personalized service.<br className="hidden md:block" />
              Level up your gaming journey with our VIP program.
            </p>
          </div>
        </section>

        {/* Current Level & Progress */}
        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="bg-gradient-to-br from-[#16181D]/90 to-[#0D0E11]/90 backdrop-blur-sm rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-12 border border-white/10">
              {/* Current Level Badge */}
              <div className="text-center mb-6 md:mb-8">
                <div className="inline-block">
                  <div className="text-5xl md:text-7xl mb-3 md:mb-4">
                    {vipLevels[getCurrentLevelIndex()].badge}
                  </div>
                  <h2 
                    className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2" 
                    style={{ 
                      fontFamily: 'Orbitron, sans-serif',
                      color: vipLevels[getCurrentLevelIndex()].color
                    }}
                  >
                    {currentLevel}
                  </h2>
                  <p className="text-white/60 text-xs md:text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    Your Current VIP Level
                  </p>
                </div>
              </div>

              {/* Progress to Next Level */}
              {nextLevel && (
                <div className="mt-8 md:mt-12">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg md:text-xl font-bold text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                      Progress to {nextLevel.name} {nextLevel.badge}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {/* Total Bets Progress */}
                    <div className="bg-white/5 rounded-xl p-4 md:p-6 border border-white/10">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-white/70 text-xs md:text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>Total Bets</span>
                        <i className="ri-money-dollar-circle-line text-[#00D8FF] text-lg md:text-xl"></i>
                      </div>
                      <div className="mb-2">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xl md:text-2xl font-bold text-white">${currentProgress.totalBets.toLocaleString()}</span>
                          <span className="text-white/50 text-xs md:text-sm">/ ${nextLevel.requirements.totalBets.toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#00D8FF] to-[#29A5FF] rounded-full transition-all duration-500"
                          style={{ width: `${calculateProgress(nextLevel.requirements.totalBets, currentProgress.totalBets)}%` }}
                        ></div>
                      </div>
                      <p className="text-white/50 text-xs mt-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {calculateProgress(nextLevel.requirements.totalBets, currentProgress.totalBets).toFixed(0)}% Complete
                      </p>
                    </div>

                    {/* Games Played Progress */}
                    <div className="bg-white/5 rounded-xl p-4 md:p-6 border border-white/10">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-white/70 text-xs md:text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>Games Played</span>
                        <i className="ri-gamepad-line text-[#4B3FAE] text-lg md:text-xl"></i>
                      </div>
                      <div className="mb-2">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xl md:text-2xl font-bold text-white">{currentProgress.gamesPlayed}</span>
                          <span className="text-white/50 text-xs md:text-sm">/ {nextLevel.requirements.gamesPlayed}</span>
                        </div>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#4B3FAE] to-[#EFBF65] rounded-full transition-all duration-500"
                          style={{ width: `${calculateProgress(nextLevel.requirements.gamesPlayed, currentProgress.gamesPlayed)}%` }}
                        ></div>
                      </div>
                      <p className="text-white/50 text-xs mt-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {calculateProgress(nextLevel.requirements.gamesPlayed, currentProgress.gamesPlayed).toFixed(0)}% Complete
                      </p>
                    </div>

                    {/* Active Days Progress */}
                    <div className="bg-white/5 rounded-xl p-4 md:p-6 border border-white/10">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-white/70 text-xs md:text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>Active Days</span>
                        <i className="ri-calendar-check-line text-[#EFBF65] text-lg md:text-xl"></i>
                      </div>
                      <div className="mb-2">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xl md:text-2xl font-bold text-white">{currentProgress.activeDays}</span>
                          <span className="text-white/50 text-xs md:text-sm">/ {nextLevel.requirements.activeDays}</span>
                        </div>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#EFBF65] to-[#00D8FF] rounded-full transition-all duration-500"
                          style={{ width: `${calculateProgress(nextLevel.requirements.activeDays, currentProgress.activeDays)}%` }}
                        ></div>
                      </div>
                      <p className="text-white/50 text-xs mt-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {calculateProgress(nextLevel.requirements.activeDays, currentProgress.activeDays).toFixed(0)}% Complete
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* All VIP Levels */}
        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white text-center mb-8 md:mb-12" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              VIP Levels & Benefits
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              {vipLevels.map((level, index) => (
                <div 
                  key={level.name}
                  className={`bg-gradient-to-br from-[#16181D]/90 to-[#0D0E11]/90 backdrop-blur-sm rounded-2xl md:rounded-3xl p-6 md:p-8 border transition-all duration-300 hover:scale-105 ${
                    level.name === currentLevel ? 'border-2' : 'border-white/10'
                  }`}
                  style={{
                    borderColor: level.name === currentLevel ? level.color : undefined
                  }}
                >
                  {/* Level Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3 md:gap-4">
                      <div className="text-4xl md:text-5xl">{level.badge}</div>
                      <div>
                        <h3 
                          className="text-xl md:text-2xl font-bold" 
                          style={{ 
                            fontFamily: 'Orbitron, sans-serif',
                            color: level.color
                          }}
                        >
                          {level.name}
                        </h3>
                        {level.name === currentLevel && (
                          <span className="text-xs text-white/60 bg-white/10 px-2 md:px-3 py-1 rounded-full inline-block mt-1">
                            Current Level
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-white/60 text-xs md:text-sm mb-6 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {level.description}
                  </p>

                  {/* Requirements */}
                  <div className="mb-6">
                    <h4 className="text-xs md:text-sm font-bold text-white/70 mb-3 uppercase tracking-wider" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Requirements
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-white/50 text-xs mb-1">Total Bets</p>
                        <p className="text-white font-bold text-sm md:text-base">${level.requirements.totalBets.toLocaleString()}</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-white/50 text-xs mb-1">Games</p>
                        <p className="text-white font-bold text-sm md:text-base">{level.requirements.gamesPlayed}</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-white/50 text-xs mb-1">Active Days</p>
                        <p className="text-white font-bold text-sm md:text-base">{level.requirements.activeDays}</p>
                      </div>
                    </div>
                  </div>

                  {/* Benefits */}
                  <div>
                    <h4 className="text-xs md:text-sm font-bold text-white/70 mb-3 uppercase tracking-wider" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Benefits
                    </h4>
                    <div className="space-y-3">
                      {level.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
                          <i className={`${benefit.icon} text-lg md:text-xl`} style={{ color: level.color }}></i>
                          <div className="flex-1">
                            <p className="text-white text-xs md:text-sm font-medium">{benefit.text}</p>
                            <p className="text-white/60 text-xs">{benefit.value}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-gradient-to-br from-[#EFBF65]/20 to-[#4B3FAE]/20 backdrop-blur-sm rounded-2xl md:rounded-3xl p-8 md:p-12 border border-[#EFBF65]/30">
              <i className="ri-vip-crown-fill text-5xl md:text-6xl text-[#EFBF65] mb-4 md:mb-6 inline-block"></i>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 md:mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                Ready to Level Up?
              </h2>
              <p className="text-white/70 text-base md:text-lg mb-6 md:mb-8 max-w-2xl mx-auto px-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Start playing now and unlock exclusive VIP benefits. The more you play, the more rewards you earn!
              </p>
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/dashboard')}
                className="bg-gradient-to-r from-[#EFBF65] to-[#4B3FAE] text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-bold text-base md:text-lg hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Start Playing Now
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
}